/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import * as React from 'react';
import {ShowHide} from "../../atoms/layout/ShowHide";
import {useIsMobile} from "../../hoc/hooks";
import {addBundleToCartAction, isLoadingById} from "../../../selectors/loader";
import {addBundleToCart} from "../../../actions/cart";
import {connect} from "react-redux";
import {getCartBundleQuantity} from "../../../selectors/my-puratos/cart";
import {OptionalBundleCell} from "../../atoms/details/OptionalBundleCell";
import {OptionalHeaderCell} from "../../atoms/details/OptionalHeaderCell";
import {OptionalBundleCartCell} from "../../atoms/details/OptionalBundleCartCell";
import Tooltip from "../../atoms/layout/Tooltip";
import {GA4InteractionType} from "../../../analytics/ga4/types/interaction";

function _OptionalBundleRow({materialNumber, bundle, translate, fallbackImage, addedQuantity, addBundleToCart, isBundleLoading}) {

    const isMobile = useIsMobile("1000px");
    const isSmallDesktop = useIsMobile("1250px") && !isMobile;

    const [quantity, setQuantity] = React.useState(1);
    const [expanded, toggle] = React.useState(false);
    const [response, setResponse] = React.useState(false);

    React.useEffect(() => {
        setQuantity(1)
    }, [bundle]);

    const active = React.useMemo(() => bundle.items.find(item => item.materialNumber == materialNumber), [bundle]);
    const relatedProducts = React.useMemo(() => bundle.items.filter(item => item.materialNumber != materialNumber), [materialNumber, bundle]);

    const orderProduct = React.useCallback(() => {
        setResponse(false);
        addBundleToCart({
            bundle: bundle,
            quantity: quantity,
            sku: materialNumber,
            type: GA4InteractionType.pdpOptionalBundle,
            callback: () => {
                setQuantity(1);
                setResponse(true);
            }
        });
    }, [bundle, quantity, materialNumber]);

    if (!active) {
        return null;
    }

    const remainingQuantity = (bundle.remainingQuantity || 0) - addedQuantity;
    const lastRowOffest = (Math.ceil(relatedProducts.length / 4) - 1) * 4;

    return <React.Fragment>
        {bundle.longPromoText &&
            <small className={"promo-label"}>
                <Tooltip placement="auto" trigger={isMobile ? "click" : "hover"} tooltip={bundle.longPromoText} enableOnTextOverflow>
                    {bundle.longPromoText}
                </Tooltip>
            </small>
        }

        <div className={"p-bundle-row" + (expanded ? " p-bundle-row--expanded" : "")}>
            <OptionalHeaderCell fallbackImage={fallbackImage} product={active}/>

            {!expanded && <React.Fragment>
                {relatedProducts.slice(0, (relatedProducts.length > (isSmallDesktop ? 2 : 3) ? (isSmallDesktop ? 1 : 2) : (isSmallDesktop ? 2 : 3))).map(relatedProduct =>
                    <div key={relatedProduct.materialNumber} className={"p-bundle-row__cell p-minicart__product"}>
                        <OptionalBundleCell translate={translate} product={relatedProduct}
                                            fallbackImage={fallbackImage}/>
                    </div>
                )}

                {relatedProducts.length > (isSmallDesktop ? 2 : 3) ?
                    <div className={"p-bundle-row__cell p-bundle-row__cell--show-more"}>
                        <div className={"btn btn-sm"} onClick={() => toggle(true)}>
                            {translate("View all products")}
                        </div>
                    </div> : null}
            </React.Fragment>}

            <ShowHide show={expanded && (isMobile)}>
                {relatedProducts.map((relatedProduct) =>
                    <div key={relatedProduct.materialNumber} className={"p-bundle-row__cell p-minicart__product"}>
                        <OptionalBundleCell translate={translate} product={relatedProduct}
                                            fallbackImage={fallbackImage}/>
                    </div>
                )}
            </ShowHide>

            {expanded && !isMobile && <div className={"p-bundle-row--show-less"} onClick={() => toggle(false)}>-</div>}

            {expanded && isMobile && <div className={"p-bundle-row__cell p-bundle-row__cell--show-more"}>
                <div className={"btn btn-sm"} onClick={() => toggle(false)}>
                    {translate("Show less")}
                </div>
            </div>}

            <OptionalBundleCartCell translate={translate} bundle={bundle} isBundleLoading={isBundleLoading}
                                    orderProduct={orderProduct} quantity={quantity} remainingQuantity={remainingQuantity}
                                    response={response} setQuantity={setQuantity} color={"SECONDARY"}/>

        </div>
        <ShowHide show={expanded && !isMobile}>
            <div className="p-bundle-row">
                {relatedProducts.map((relatedProduct, index) =>
                    <div key={relatedProduct.materialNumber}
                         className={"p-bundle-row__cell  p-minicart__product " + (index % 4 === 0 ? "p-bundle-row-cell--first-of-line" : "")
                         + (index < lastRowOffest ? " p-bundle-row-cell--lined-cell" : "")}>
                        <OptionalBundleCell translate={translate} product={relatedProduct}
                                            fallbackImage={fallbackImage}/>
                    </div>
                )}
            </div>
        </ShowHide>
    </React.Fragment>
}

const mapStateToProps = (state, ownProps) => {
    return {
        isBundleLoading: isLoadingById(state, addBundleToCartAction(ownProps.bundle && ownProps.bundle.bundleId)),
        addedQuantity: getCartBundleQuantity(state, ownProps.bundle.bundleId)
    };
};

const mapActionsToProps = {
    addBundleToCart
};

export const OptionalBundleRow = connect(mapStateToProps, mapActionsToProps)(_OptionalBundleRow);
